/**
 * The 14 conditions (SRD 5.2, CC-BY-4.0), written out because they are the
 * single most looked-up thing at a table and nobody wants to open a book for
 * "what does Restrained do again?".
 */

export interface ConditionDef {
  id: string
  name: string
  /** One line for the tooltip and the initiative tracker. */
  summary: string
  effects: string[]
}

export const CONDITIONS_DATA: ConditionDef[] = [
  {
    id: 'blinded', name: 'Blinded',
    summary: "Can't see. You attack at disadvantage, and attacks against you have advantage.",
    effects: [
      "You can't see and automatically fail any ability check that requires sight.",
      'Attack rolls against you have Advantage, and your attack rolls have Disadvantage.',
    ],
  },
  {
    id: 'charmed', name: 'Charmed',
    summary: "Can't harm the charmer, who finds it easier to talk you round.",
    effects: [
      "You can't attack the charmer or target them with damaging abilities or magical effects.",
      'The charmer has Advantage on ability checks to interact with you socially.',
    ],
  },
  {
    id: 'deafened', name: 'Deafened',
    summary: "Can't hear, and fail anything that depends on hearing.",
    effects: ["You can't hear and automatically fail any ability check that requires hearing."],
  },
  {
    id: 'frightened', name: 'Frightened',
    summary: "Disadvantage while you can see the source, and you can't approach it.",
    effects: [
      'You have Disadvantage on ability checks and attack rolls while the source of your fear is within line of sight.',
      "You can't willingly move closer to the source of your fear.",
    ],
  },
  {
    id: 'grappled', name: 'Grappled',
    summary: 'Speed 0, and disadvantage attacking anyone but whoever has hold of you.',
    effects: [
      'Your Speed is 0 and cannot increase.',
      'You have Disadvantage on attack rolls against any target other than the grappler.',
      'The condition ends if the grappler is Incapacitated, or if something moves you out of reach of it.',
    ],
  },
  {
    id: 'incapacitated', name: 'Incapacitated',
    summary: 'No actions, no reactions, no concentration, no speech.',
    effects: [
      "You can't take any action, Bonus Action, or Reaction.",
      'Your Concentration is broken.',
      "You can't speak.",
      'If you become Incapacitated when you roll Initiative, you have Disadvantage on that roll.',
    ],
  },
  {
    id: 'invisible', name: 'Invisible',
    summary: "Can't be seen. You attack with advantage; attacks against you have disadvantage.",
    effects: [
      'You cannot be seen without the aid of magic or a special sense, and you count as Heavily Obscured for hiding.',
      'Attack rolls against you have Disadvantage, and your attack rolls have Advantage.',
      'If you are Invisible when you roll Initiative, you have Advantage on that roll.',
    ],
  },
  {
    id: 'paralyzed', name: 'Paralyzed',
    summary: 'Incapacitated, Speed 0, and anything hitting you from close range crits.',
    effects: [
      'You have the Incapacitated condition and your Speed is 0.',
      'You automatically fail Strength and Dexterity saving throws.',
      'Attack rolls against you have Advantage.',
      'Any attack that hits you from within 5 feet is a Critical Hit.',
    ],
  },
  {
    id: 'petrified', name: 'Petrified',
    summary: 'Turned to stone. Incapacitated and unaware, but resistant to everything.',
    effects: [
      'You and your gear are turned to solid stone; your weight increases tenfold and you stop aging.',
      'You have the Incapacitated condition, your Speed is 0, and you are unaware of your surroundings.',
      'You automatically fail Strength and Dexterity saving throws, and attack rolls against you have Advantage.',
      'You have Resistance to all damage and Immunity to the Poisoned condition.',
    ],
  },
  {
    id: 'poisoned', name: 'Poisoned',
    summary: 'Disadvantage on attack rolls and ability checks.',
    effects: ['You have Disadvantage on attack rolls and ability checks.'],
  },
  {
    id: 'prone', name: 'Prone',
    summary: 'On the floor. You attack at disadvantage; nearby attackers get advantage.',
    effects: [
      'Your only movement option is to crawl, which costs extra movement, unless you stand up and end the condition.',
      'You have Disadvantage on attack rolls.',
      'An attack roll against you has Advantage if the attacker is within 5 feet, and Disadvantage otherwise.',
    ],
  },
  {
    id: 'restrained', name: 'Restrained',
    summary: 'Speed 0, you attack at disadvantage, attacks against you have advantage.',
    effects: [
      'Your Speed is 0 and cannot increase.',
      'Attack rolls against you have Advantage, and your attack rolls have Disadvantage.',
      'You have Disadvantage on Dexterity saving throws.',
    ],
  },
  {
    id: 'stunned', name: 'Stunned',
    summary: 'Incapacitated, Speed 0, and easy to hit.',
    effects: [
      'You have the Incapacitated condition and your Speed is 0.',
      'You automatically fail Strength and Dexterity saving throws.',
      'Attack rolls against you have Advantage.',
    ],
  },
  {
    id: 'unconscious', name: 'Unconscious',
    summary: 'Out cold and prone. Close-range hits are critical.',
    effects: [
      'You have the Incapacitated and Prone conditions, your Speed is 0, and you drop whatever you are holding.',
      'You are unaware of your surroundings.',
      'You automatically fail Strength and Dexterity saving throws.',
      'Attack rolls against you have Advantage, and any attack that hits you from within 5 feet is a Critical Hit.',
    ],
  },
]

export const CONDITIONS_BY_ID = new Map(CONDITIONS_DATA.map((c) => [c.id, c]))
export const CONDITION_BY_NAME = new Map(CONDITIONS_DATA.map((c) => [c.name, c]))

/** Exhaustion is a tracked number rather than an on/off condition in 2024. */
export const EXHAUSTION_RULE = {
  name: 'Exhaustion',
  summary: 'Levels 1-6. Each level makes everything worse; level 6 is death.',
  effects: [
    'You take a −2 penalty to d20 Tests for each level of Exhaustion.',
    'Your Speed drops by 5 feet for each level of Exhaustion.',
    'At level 6 you die.',
    'Finishing a Long Rest removes one level.',
  ],
}
